// covered-by: frontends/carrie/src/routes/tickets/routes/ticket/__tests__/useTicketPageRoute.test.ts
//
// TicketView is shared by ticketRoute (/tickets/:id/view) and
// ticketCommentRoute (/tickets/:id/:boardId/:commentId). Binding to
// ticketRoute.useParams()/useSearch()/useNavigate() throws TanStack's
// "Invariant failed: Could not find an active match from
// /tickets/$ticketId/view" on cold comment deep links. Read identity from
// loose route params/search instead.

import {
  useLoaderData,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";

export type TicketPageParams = {
  ticketId: string;
  boardId?: string;
  commentId?: string;
};

export type TicketPageSearch = {
  version?: number;
  rating?: number;
  integration?: boolean;
  view?: "workflow" | "grid" | "list";
};

export type TicketPageLoaderData = {
  /** Sibling tickets for prev/next — only present on ticketRoute. */
  tickets?: { _id: string }[];
  commentExists?: boolean;
};

/** Params for whichever classic ticket page route is active. */
export function useTicketPageParams(): TicketPageParams {
  const params = useParams({ strict: false }) as {
    ticketId?: string;
    boardId?: string;
    commentId?: string;
  };
  return {
    ticketId: params.ticketId?.trim() ?? "",
    boardId: params.boardId?.trim() || undefined,
    commentId: params.commentId?.trim() || undefined,
  };
}

/** Search for the classic ticket page (empty on the comment deep-link route). */
export function useTicketPageSearch(): TicketPageSearch {
  return useSearch({ strict: false }) as TicketPageSearch;
}

/**
 * Loader data for the active ticket page match.
 * Comment deep links do not include the sibling-tickets list from ticketRoute.
 */
export function useTicketPageLoaderData(): TicketPageLoaderData {
  return (useLoaderData({ strict: false }) ?? {}) as TicketPageLoaderData;
}

/** Navigate helper that is not bound to ticketRoute's match. */
export function useTicketPageNavigate() {
  return useNavigate();
}
